"use client";
import React, { useEffect, useRef } from 'react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';

interface WhyChooseUsSectionProps {
  isVisible: boolean;
  onVisibilityChange: (visible: boolean) => void;
}

export default function WhyChooseUsSection({ isVisible, onVisibilityChange }: WhyChooseUsSectionProps) {
  const sectionRef = useRef(null);
  const t = useTranslations();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          onVisibilityChange(true);
        }
      },
      {
        threshold: 0.02,
        rootMargin: '0px 0px 50px 0px'
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, [onVisibilityChange]);

  return (
    <>
      {/* WhyChooseUs Section Styles */}
      <style dangerouslySetInnerHTML={{__html: `
        .why-choose-bg-1 {
          filter: blur(150px);
        }
        
        @media (max-width: 768px) {
          .why-choose-grid {
            grid-template-columns: 1fr !important;
            grid-template-rows: repeat(6, auto) !important;
            min-height: auto !important;
            gap: 20px !important;
          }
          
          .why-choose-grid > div {
            grid-area: auto !important;
          }
          
          .why-choose-box-1 {
            padding: 32px 20px 100px 20px !important;
          }
          
          .why-choose-box-1 .mjc-blur-mobile {
            width: 100% !important;
            height: 120px !important;
            left: 0 !important;
            bottom: -20px !important;
          }
          
          .why-choose-box-2 {
            flex-direction: column !important;
            text-align: center !important;
            padding: 32px 20px !important;
          }
          
          .why-choose-box-2 .text-content {
            max-width: 100% !important;
            padding-right: 0 !important;
            margin-bottom: 20px !important;
          }
          
          .why-choose-box-2 .world-container {
            position: relative !important;
            width: 100% !important;
            height: 150px !important;
            bottom: auto !important;
            right: auto !important;
          }
          
          .section-padding {
            padding: 80px 12px !important;
          }
          
          .why-choose-bg-1 {
            filter: none !important;
            background: radial-gradient(circle at center, rgba(217, 13, 52, 0.3) 0%, rgba(217, 13, 52, 0.2) 20%, rgba(217, 13, 52, 0.12) 40%, rgba(217, 13, 52, 0.06) 60%, rgba(217, 13, 52, 0.03) 80%, transparent 100%) !important;
          }
        }

        @media (max-width: 480px) {
          .section-title {
            font-size: 32px !important;
          }
          
          .section-padding {
            padding: 60px 16px !important;
          }
        }
      `}} />

      <section 
        ref={sectionRef} 
        style={{
          background: "#000",
          padding: "80px 24px",
          position: "relative",
          overflow: "hidden",
        }}
        className="section-padding"
      >
        {/* Background gradient */}
        <div 
          className="why-choose-bg-1"
          style={{
            position: "absolute",
            left: "50%",
            top: "300px",
            transform: "translateX(-50%)",
            width: "600px",
            height: "400px",
            background: "radial-gradient(50% 50% at 50% 50%, #D90D34 0%, rgba(217, 13, 52, 0) 100%)",
            zIndex: 1,
            opacity: isVisible ? 0.3 : 0,
            transition: "opacity 1.5s ease-out",
            transitionDelay: "0.2s",
          }} 
        />

        <div style={{
          maxWidth: "1400px",
          margin: "0 auto",
          position: "relative",
          zIndex: 10,
        }}>
          {/* Title and Description */}
          <div style={{ 
            textAlign: "center", 
            marginBottom: "80px",
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0px)' : 'translateY(30px)',
            transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          }}>
            <h2 style={{
              fontSize: "42px",
              fontWeight: 600,
              color: "#fff",
              lineHeight: 1.2,
              marginBottom: "32px",
            }}
            className="section-title">
              {t('vilnius.whyChooseUs.title')}
            </h2>
            
            <p style={{
              fontSize: "18px",
              color: "rgba(255,255,255,0.85)",
              lineHeight: 1.6,
              maxWidth: "800px",
              margin: "0 auto",
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0px)' : 'translateY(30px)',
              transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              transitionDelay: "0.1s",
            }}>
              {t('vilnius.whyChooseUs.intro')}
            </p>
          </div>

          {/* Complex 3x4 Grid */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gridTemplateRows: "1fr 1fr 1fr 1fr",
            rowGap: "40px",
            columnGap: "24px",
            maxWidth: "900px",
            margin: "0 auto",
            position: "relative",
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0px)' : 'translateY(30px)',
            transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
            transitionDelay: "0.2s",
          }}
          className="why-choose-grid">

            {/* Red circle for Box 1 - positioned relative to grid */}
            <div style={{
              position: "absolute",
              top: "-30px",
              left: "20px",
              width: "60px",
              height: "60px",
              zIndex: 10,
            }}>
              <Image
                src="/images/components/red-circle.png"
                alt="Red circle"
                width={60}
                height={60}
                style={{ 
                  objectFit: "contain",
                }}
              />
              {/* Calendar Icon */}
              <div style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: "28px",
                height: "28px",
                zIndex: 11,
              }}>
                <Image 
                  src="/images/components/international/calendar.png"
                  alt="Calendar icon"
                  width={28}
                  height={28}
                  style={{ 
                    objectFit: "contain",
                  }}
                />
              </div>
            </div>

            {/* Box 1 - 1x2 (1 column wide, 2 rows tall) */}
            <div style={{
              gridArea: "1 / 1 / 3 / 2",
              background: "linear-gradient(99.27deg, #000000 10.92%, #0B0B0B 93.71%)",
              border: "1px solid #262626",
              borderRadius: "12px",
              padding: "32px 32px 32px 20px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "flex-start",
              textAlign: "left",
              position: "relative",
              overflow: "hidden",
              zIndex: 1,
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'scale(1) translateY(0px)' : 'scale(0.95) translateY(20px)',
              transition: 'all 0.9s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              transitionDelay: "0.1s",
            }}
            className="why-choose-box-1">
              <h3 style={{
                fontSize: "18px",
                fontWeight: 400,
                color: "#fff",
                marginBottom: "14px",
                marginTop: "20px",
              }}>
                {t('vilnius.whyChooseUs.reasons.experience.title')}
              </h3>
              <p style={{
                fontSize: "12px",
                color: "rgba(255,255,255,0.7)",
                lineHeight: 1.5,
                marginBottom: "24px",
              }}>
                {t('vilnius.whyChooseUs.reasons.experience.description')}
              </p>
              
              {/* MJC Blur Image */}
              <div style={{
                width: "350%",
                height: "260px",
                position: "absolute",
                bottom: "-40px",
                left: "-150%",
                zIndex: 0,
              }}
              className="mjc-blur-mobile">
                <Image
                  src="/images/components/mjc-blur.png"
                  alt="MJC Blur"
                  fill
                  style={{ 
                    objectFit: "contain",
                  }}
                />
              </div>
            </div>

            {/* Box 2 - 2x1 (2 columns wide, 1 row tall) */}
            <div style={{
              gridArea: "1 / 2 / 2 / 4",
              background: "linear-gradient(99.27deg, #000000 30.92%, #0B0B0B 93.71%)",
              border: "1px solid #262626",
              borderRadius: "12px",
              padding: "32px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              textAlign: "left",
              position: "relative",
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateX(0px)' : 'translateX(40px)',
              transition: 'all 1s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              transitionDelay: "0.4s",
            }}
            className="why-choose-box-2">
              {/* Red circle positioned outside the top border */}
              <div style={{
                position: "absolute",
                top: "-30px",
                left: "20px",
                width: "60px",
                height: "60px",
                zIndex: 2,
              }}>
                <Image
                  src="/images/components/red-circle.png"
                  alt="Red circle"
                  width={60}
                  height={60}
                  style={{ 
                    objectFit: "contain",
                  }}
                />
                {/* Network Icon */}
                <div style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: "28px",
                  height: "28px",
                  zIndex: 3,
                }}>
                  <Image 
                    src="/images/components/international/network.png"
                    alt="Network icon"
                    width={28}
                    height={28}
                    style={{ 
                      objectFit: "contain",
                    }}
                  />
                </div>
              </div>
              
              {/* Left side - Text content */}
              <div style={{ 
                flex: 1, 
                paddingRight: "32px",
                maxWidth: "calc(100% - 260px)",
                zIndex: 2,
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateX(0px)' : 'translateX(-40px)',
                transition: 'all 1s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                transitionDelay: "0.8s",
              }}
              className="text-content">
                <h3 style={{
                  fontSize: "18px",
                  fontWeight: 400,
                  color: "#fff",
                  marginBottom: "14px",
                  marginTop: "20px",
                }}>
                  {t('vilnius.whyChooseUs.reasons.knowledge.title')}
                </h3>
                <p style={{
                  fontSize: "12px",
                  color: "rgba(255,255,255,0.7)",
                  lineHeight: 1.5,
                }}>
                  {t('vilnius.whyChooseUs.reasons.knowledge.description')}
                </p>
              </div>

              {/* Right side - World image */}
              <div style={{
                width: "280px",
                height: "200px",
                position: "absolute",
                bottom: "1px",
                right: "-20px",
                flexShrink: 0,
                zIndex: 1,
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateX(0px)' : 'translateX(40px)',
                transition: 'all 1s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                transitionDelay: "1s",
              }}
              className="world-container">
                <Image
                  src="/images/components/international/world.png"
                  alt="World map"
                  fill
                  style={{ 
                    objectFit: "contain",
                  }}
                />
                
                {/* Glowing red dots */}
                <div style={{
                  position: "absolute",
                  top: "30%",
                  left: "25%",
                  width: "6px",
                  height: "6px",
                  backgroundColor: "#D90D34",
                  borderRadius: "50%",
                  boxShadow: "0 0 8px #D90D34, 0 0 12px #D90D34, 0 0 16px #D90D34",
                  zIndex: 2,
                }} />
                
                <div style={{
                  position: "absolute",
                  top: "60%",
                  left: "70%",
                  width: "6px",
                  height: "6px",
                  backgroundColor: "#D90D34",
                  borderRadius: "50%",
                  boxShadow: "0 0 8px #D90D34, 0 0 12px #D90D34, 0 0 16px #D90D34",
                  zIndex: 2,
                }} />
                
                <div style={{
                  position: "absolute",
                  top: "45%",
                  left: "50%",
                  width: "6px",
                  height: "6px",
                  backgroundColor: "#D90D34",
                  borderRadius: "50%",
                  boxShadow: "0 0 8px #D90D34, 0 0 12px #D90D34, 0 0 16px #D90D34",
                  zIndex: 2,
                }} />
              </div>
            </div>

            {/* Box 3 - 1x1 (square) */}
            <div style={{
              gridArea: "2 / 2 / 3 / 3",
              background: "linear-gradient(99.27deg, #000000 30.92%, #0B0B0B 93.71%)",
              border: "1px solid #262626",
              borderRadius: "12px",
              padding: "32px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "flex-start",
              textAlign: "left",
              position: "relative",
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0px)' : 'translateY(30px)',
              transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              transitionDelay: "0.6s",
            }}>
              {/* Red circle positioned outside the top border */}
              <div style={{
                position: "absolute",
                top: "-30px",
                left: "20px",
                width: "60px",
                height: "60px",
                zIndex: 2,
              }}>
                <Image
                  src="/images/components/red-circle.png"
                  alt="Red circle"
                  width={60}
                  height={60}
                  style={{ 
                    objectFit: "contain",
                  }}
                />
                {/* Safety Icon */}
                <div style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: "28px",
                  height: "28px",
                  zIndex: 3,
                }}>
                  <Image 
                    src="/images/components/international/safety.png"
                    alt="Safety icon"
                    width={28}
                    height={28}
                    style={{ 
                      objectFit: "contain",
                    }}
                  />
                </div>
              </div>
              
              <div>
                <h3 style={{
                  fontSize: "18px",
                  fontWeight: 400,
                  color: "#fff",
                  marginBottom: "14px",
                  marginTop: "20px",
                }}>
                  {t('vilnius.whyChooseUs.reasons.speed.title')}
                </h3>
                <p style={{
                  fontSize: "12px",
                  color: "rgba(255,255,255,0.7)",
                  lineHeight: 1.5,
                }}>
                  {t('vilnius.whyChooseUs.reasons.speed.description')}
                </p>
              </div>
            </div>

            {/* Box 4 - 1x1 (square) */}
            <div style={{
              gridArea: "2 / 3 / 3 / 4",
              background: "linear-gradient(99.27deg, #000000 30.92%, #0B0B0B 93.71%)",
              border: "1px solid #262626",
              borderRadius: "12px",
              padding: "32px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "flex-start",
              textAlign: "left",
              position: "relative",
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0px)' : 'translateY(30px)',
              transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              transitionDelay: "0.8s",
            }}>
              {/* Red circle positioned outside the top border */}
              <div style={{
                position: "absolute",
                top: "-30px",
                left: "20px",
                width: "60px",
                height: "60px",
                zIndex: 2,
              }}>
                <Image
                  src="/images/components/red-circle.png"
                  alt="Red circle"
                  width={60}
                  height={60}
                  style={{ 
                    objectFit: "contain",
                  }}
                />
                {/* Gear Icon */}
                <div style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: "28px",
                  height: "28px",
                  zIndex: 3,
                }}>
                  <Image 
                    src="/images/components/international/gear.png"
                    alt="Gear icon"
                    width={28}
                    height={28}
                    style={{ 
                      objectFit: "contain",
                    }}
                  />
                </div>
              </div>
              
              <div>
                <h3 style={{
                  fontSize: "18px",
                  fontWeight: 400,
                  color: "#fff",
                  marginBottom: "14px",
                  marginTop: "20px",
                }}>
                  {t('vilnius.whyChooseUs.reasons.pricing.title')}
                </h3>
                <p style={{
                  fontSize: "12px",
                  color: "rgba(255,255,255,0.7)",
                  lineHeight: 1.5,
                }}>
                  {t('vilnius.whyChooseUs.reasons.pricing.description')}
                </p>
              </div>
            </div>

            {/* Box 5 - 2x1 (2 columns wide, 1 row tall) */}
            <div style={{
              gridArea: "3 / 2 / 4 / 4",
              background: "linear-gradient(99.27deg, #000000 30.92%, #0B0B0B 93.71%)",
              border: "1px solid #262626",
              borderRadius: "12px",
              padding: "32px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "flex-start",
              textAlign: "left",
              position: "relative",
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateX(0px)' : 'translateX(-40px)',
              transition: 'all 1s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              transitionDelay: "1s",
            }}>
              {/* Red circle positioned outside the top border */}
              <div style={{
                position: "absolute",
                top: "-30px",
                left: "20px",
                width: "60px",
                height: "60px",
                zIndex: 2,
              }}>
                <Image
                  src="/images/components/red-circle.png"
                  alt="Red circle"
                  width={60}
                  height={60}
                  style={{ 
                    objectFit: "contain",
                  }}
                />
                {/* Network Icon */}
                <div style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: "28px",
                  height: "28px",
                  zIndex: 3,
                }}>
                  <Image 
                    src="/images/components/international/network.png"
                    alt="Network icon"
                    width={28}
                    height={28}
                    style={{ 
                      objectFit: "contain",
                    }}
                  />
                </div>
              </div>
              
              <div>
                <h3 style={{
                  fontSize: "18px",
                  fontWeight: 400,
                  color: "#fff",
                  marginBottom: "14px",
                  marginTop: "20px",
                }}>
                  {t('oldFurnitureRemoval.whyChooseUs.benefits.professionalDismantling.title')}
                </h3>
                <p style={{
                  fontSize: "12px",
                  color: "rgba(255,255,255,0.7)",
                  lineHeight: 1.5,
                }}>
                  {t('oldFurnitureRemoval.whyChooseUs.benefits.professionalDismantling.description')}
                </p>
              </div>
            </div>

            {/* Box 6 - 1x1 (square) */}
            <div style={{
              gridArea: "4 / 3 / 5 / 4",
              background: "linear-gradient(99.27deg, #000000 30.92%, #0B0B0B 93.71%)",
              border: "1px solid #262626",
              borderRadius: "12px",
              padding: "32px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "flex-start",
              textAlign: "left",
              position: "relative",
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'scale(1) translateY(0px)' : 'scale(0.95) translateY(20px)',
              transition: 'all 0.9s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              transitionDelay: "1.2s",
            }}>
              {/* Red circle positioned outside the top border */}
              <div style={{
                position: "absolute",
                top: "-30px",
                left: "20px",
                width: "60px",
                height: "60px",
                zIndex: 2,
              }}>
                <Image
                  src="/images/components/red-circle.png"
                  alt="Red circle"
                  width={60}
                  height={60}
                  style={{ 
                    objectFit: "contain",
                  }}
                />
                {/* Transparency Icon */}
                <div style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: "28px",
                  height: "28px",
                  zIndex: 3,
                }}>
                  <Image 
                    src="/images/components/international/transparency.png"
                    alt="Transparency icon"
                    width={28}
                    height={28}
                    style={{ 
                      objectFit: "contain",
                    }}
                  />
                </div>
              </div>
              
              <div>
                <h3 style={{
                  fontSize: "18px",
                  fontWeight: 400,
                  color: "#fff",
                  marginBottom: "14px",
                  marginTop: "20px",
                }}>
                  {t('oldFurnitureRemoval.whyChooseUs.benefits.safeRemoval.title')}
                </h3>
                <p style={{
                  fontSize: "12px",
                  color: "rgba(255,255,255,0.7)",
                  lineHeight: 1.5,
                }}>
                  {t('oldFurnitureRemoval.whyChooseUs.benefits.safeRemoval.description')}
                </p>
              </div>
            </div>

            {/* Box 7 - 1x1 (square) - responsibleApproach */}
            <div style={{
              gridArea: "4 / 1 / 5 / 2",
              background: "linear-gradient(99.27deg, #000000 30.92%, #0B0B0B 93.71%)",
              border: "1px solid #262626",
              borderRadius: "12px",
              padding: "32px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "flex-start",
              textAlign: "left",
              position: "relative",
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0px)' : 'translateY(30px)',
              transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              transitionDelay: "1.4s",
            }}>
              {/* Red circle positioned outside the top border */}
              <div style={{
                position: "absolute",
                top: "-30px",
                left: "20px",
                width: "60px",
                height: "60px",
                zIndex: 2,
              }}>
                <Image
                  src="/images/components/red-circle.png"
                  alt="Red circle"
                  width={60}
                  height={60}
                  style={{ 
                    objectFit: "contain",
                  }}
                />
                {/* Shield Icon */}
                <div style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: "28px",
                  height: "28px",
                  zIndex: 3,
                }}>
                  <Image 
                    src="/images/components/international/shield.png"
                    alt="Shield icon"
                    width={28}
                    height={28}
                    style={{ 
                      objectFit: "contain",
                    }}
                  />
                </div>
              </div>
              
              <div>
                <h3 style={{
                  fontSize: "18px",
                  fontWeight: 400,
                  color: "#fff",
                  marginBottom: "14px",
                  marginTop: "20px",
                }}>
                  {t('oldFurnitureRemoval.whyChooseUs.benefits.responsibleApproach.title')}
                </h3>
                <p style={{
                  fontSize: "12px",
                  color: "rgba(255,255,255,0.7)",
                  lineHeight: 1.5,
                }}>
                  {t('oldFurnitureRemoval.whyChooseUs.benefits.responsibleApproach.description')}
                </p>
              </div>
            </div>

            {/* Box 8 - 1x1 (square) - flexibleSchedule */}
            <div style={{
              gridArea: "4 / 2 / 5 / 3",
              background: "linear-gradient(99.27deg, #000000 30.92%, #0B0B0B 93.71%)",
              border: "1px solid #262626",
              borderRadius: "12px",
              padding: "32px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "flex-start",
              textAlign: "left",
              position: "relative",
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0px)' : 'translateY(30px)',
              transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              transitionDelay: "1.6s",
            }}>
              {/* Red circle positioned outside the top border */}
              <div style={{
                position: "absolute",
                top: "-30px",
                left: "20px",
                width: "60px",
                height: "60px",
                zIndex: 2,
              }}>
                <Image
                  src="/images/components/red-circle.png"
                  alt="Red circle"
                  width={60}
                  height={60}
                  style={{ 
                    objectFit: "contain",
                  }}
                />
                {/* Calendar Icon */}
                <div style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: "28px",
                  height: "28px",
                  zIndex: 3,
                }}>
                  <Image 
                    src="/images/components/international/calendar.png"
                    alt="Calendar icon"
                    width={28}
                    height={28}
                    style={{ 
                      objectFit: "contain",
                    }}
                  />
                </div>
              </div>
              
              <div>
                <h3 style={{
                  fontSize: "18px",
                  fontWeight: 400,
                  color: "#fff",
                  marginBottom: "14px",
                  marginTop: "20px",
                }}>
                  {t('oldFurnitureRemoval.whyChooseUs.benefits.flexibleSchedule.title')}
                </h3>
                <p style={{
                  fontSize: "12px",
                  color: "rgba(255,255,255,0.7)",
                  lineHeight: 1.5,
                }}>
                  {t('oldFurnitureRemoval.whyChooseUs.benefits.flexibleSchedule.description')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
} 